import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { db, storage, functions } from '../firebase-config';
import {
  collection, query, where, onSnapshot, doc, setDoc, updateDoc, serverTimestamp, deleteDoc, getDoc, getDocs,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, deleteObject, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import DashboardAppearanceSettings from './DashboardAppearanceSettings';

// ─── Status badge helper ───────────────────────────────────────────────────────
const STATUS_CONFIG = {
  pending: { label: 'Awaiting owner', bg: '#dbeafe', color: '#1e40af', dot: '#3b82f6' },
  pending_review: { label: 'Awaiting owner', bg: '#dbeafe', color: '#1e40af', dot: '#3b82f6' },
  approved: { label: 'Approved', bg: '#d1fae5', color: '#065f46', dot: '#10b981' },
  converted_to_tenant: { label: 'Converted', bg: '#dcfce7', color: '#166534', dot: '#22c55e' },
  rejected: { label: 'Rejected', bg: '#fee2e2', color: '#991b1b', dot: '#ef4444' },
  extraction_failed: { label: 'Processing Error', bg: '#fce7f3', color: '#9d174d', dot: '#ec4899' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      background: cfg.bg, color: cfg.color,
      padding: '4px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 600,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.dot, display: 'inline-block' }} />
      {cfg.label}
    </span>
  );
}

function getGreetingLabel() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function getDisplayName(fullName, email) {
  if (fullName?.trim()) {
    return fullName.trim().split(/\s+/)[0];
  }
  if (email?.includes('@')) {
    return email.split('@')[0];
  }
  return 'there';
}

/** True if a converted applicant still reflects an active lease placement (tenant linked to the same unit/owner). */
function isCurrentPlacement(applicant, tenantDetails) {
  if (applicant.placementActive === false) return false;
  if (!applicant.tenantId) return false;
  if (!tenantDetails) {
    return applicant.placementActive === true;
  }
  const t = tenantDetails;
  if (!t.unitId || !t.ownerId) return false;
  if (applicant.unitId && String(t.unitId) !== String(applicant.unitId)) return false;
  if (applicant.ownerId && String(t.ownerId) !== String(applicant.ownerId)) return false;
  return true;
}

function matchesRelationshipsSearch(row, searchTerm) {
  const q = (searchTerm || '').toLowerCase().trim();
  if (!q) return true;
  const ownerName = (row.ownerDetails?.fullName || '').toLowerCase();
  const ownerEmail = (row.ownerDetails?.email || '').toLowerCase();
  const unit = String(row.unitNumber || row.unitDetails?.unitNumber || '').toLowerCase();
  const addr = (row.unitDetails?.address || '').toLowerCase();
  const id = (row.id || '').toLowerCase();
  return (
    ownerName.includes(q) ||
    ownerEmail.includes(q) ||
    unit.includes(q) ||
    addr.includes(q) ||
    id.includes(q)
  );
}

function SectionIntro({ eyebrow, title, description }) {
  return (
    <div
      className="td-section-intro"
      style={{
      background: 'linear-gradient(135deg, #f8faff 0%, #eef2ff 55%, #f5f3ff 100%)',
      border: '1px solid #dbe4ff',
      borderRadius: 18,
      padding: '24px 26px',
      marginBottom: 24,
      boxShadow: '0 10px 28px rgba(79, 70, 229, 0.08)',
    }}
    >
      {eyebrow && (
        <p style={{ margin: '0 0 8px 0', color: '#6366f1', fontWeight: 700, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {eyebrow}
        </p>
      )}
      <h2 style={{ margin: '0 0 8px 0', color: '#111827', fontSize: 28, fontWeight: 800 }}>
        {title}
      </h2>
      {description && (
        <p style={{ margin: 0, color: '#4b5563', fontSize: 15, lineHeight: 1.6, maxWidth: 720 }}>
          {description}
        </p>
      )}
    </div>
  );
}

// ─── Upload Zone ───────────────────────────────────────────────────────────────
const INITIAL_FORM = {
  firstName: '', lastName: '', email: '', phone: '', monthlyIncome: '', creditScore: '',
  currentEmployer: '', employmentStatus: '', proposedMoveInDate: '',
  numberOfOccupants: '', hasPets: null, smokingStatus: '',
  priorEvictions: null, additionalNotes: '',
};

/** PDF extraction overlay progress bar duration (matches typical extract latency). */
const EXTRACT_BAR_MS = 10000;

function UploadZone({ realtorId, realtorEmail, onUploaded, registeredOwners, formData, setFormData, activeTenants }) {
  const [file, setFile] = useState(null);
  const [additionalFiles, setAdditionalFiles] = useState([]);
  const [additionalUploadStatus, setAdditionalUploadStatus] = useState({ name: '', progress: 0 });
  const [extractUiOpen, setExtractUiOpen] = useState(false);
  const [extractProgress, setExtractProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [selectedTenant, setSelectedTenant] = useState('');
  const [dragging, setDragging] = useState(false);
  const [extractionError, setExtractionError] = useState('');
  const fileInputRef = useRef();
  const extractSessionStartRef = useRef(0);
  const extractCloseTimerRef = useRef(null);

  useEffect(() => {
    if (!extractUiOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [extractUiOpen]);

  useEffect(() => {
    if (!extractUiOpen) {
      setExtractProgress(0);
      return undefined;
    }
    const start = extractSessionStartRef.current;
    const id = setInterval(() => {
      const p = Math.min(100, ((Date.now() - start) / EXTRACT_BAR_MS) * 100);
      setExtractProgress(p);
      if (p >= 100) clearInterval(id);
    }, 50);
    return () => clearInterval(id);
  }, [extractUiOpen]);

  useEffect(() => () => {
    if (extractCloseTimerRef.current) {
      clearTimeout(extractCloseTimerRef.current);
      extractCloseTimerRef.current = null;
    }
  }, []);

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const normalizeNumericForNumberInput = (raw) => {
    if (raw === null || raw === undefined) return '';
    const s = String(raw).trim();
    if (!s) return '';
    // Keep digits, dot, and minus; strip commas/$/spaces/etc. so <input type="number"> can parse it.
    const cleaned = s.replace(/[^\d.-]/g, '');
    if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === '-.') return '';
    const n = Number(cleaned);
    if (!Number.isFinite(n)) return '';
    return String(n);
  };

  const normalizeDateForDateInput = (raw) => {
    if (!raw) return '';
    const s = String(raw).trim();
    if (!s) return '';
    // Already ISO yyyy-MM-dd (what <input type="date"> requires)
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    // Common extracted format: MM/DD/YYYY
    const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdy) {
      const mm = String(mdy[1]).padStart(2, '0');
      const dd = String(mdy[2]).padStart(2, '0');
      const yyyy = mdy[3];
      return `${yyyy}-${mm}-${dd}`;
    }
    // Best-effort parse (handles "Nov 15 2017", etc.)
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // Handle tenant selection
  const handleTenantSelect = (tenantId) => {
    const tenant = activeTenants.find(t => t.id === tenantId);
    if (!tenant) return;
    
    const tenantData = tenant.extractedData || {};
    const preFilledData = {
      firstName: tenantData.firstName || tenantData.fullName?.split(' ')[0] || '',
      lastName: tenantData.lastName || tenantData.fullName?.split(' ').slice(1).join(' ') || '',
      email: tenantData.email || '',
      phone: tenantData.phone || '',
      monthlyIncome: tenantData.monthlyIncome?.toString() || '',
      creditScore: tenantData.creditScore?.toString() || '',
      currentEmployer: tenantData.currentEmployer || '',
      employmentStatus: tenantData.employmentStatus || '',
      proposedMoveInDate: tenantData.proposedMoveInDate || '',
      numberOfOccupants: tenantData.numberOfOccupants || '',
      hasPets: tenantData.hasPets || null,
      smokingStatus: tenantData.smokingStatus || '',
      priorEvictions: tenantData.priorEvictions || null,
      additionalNotes: tenantData.additionalNotes || '',
      originalTenantId: tenant.id,
      originalTenantName: tenantData.fullName || 'Unknown Tenant'
    };
    
    setFormData(preFilledData);
    setSelectedTenant(tenantId);
  };

  const handleFile = async (f) => {
    if (!f || f.type !== 'application/pdf') return;
    if (extractCloseTimerRef.current) {
      clearTimeout(extractCloseTimerRef.current);
      extractCloseTimerRef.current = null;
    }
    setFile(f);
    setDragging(false);
    setExtractionError('');
    extractSessionStartRef.current = Date.now();
    setExtractUiOpen(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(f);
      });
      const extractPdfData = httpsCallable(functions, 'extractPdfData');
      const resp = await extractPdfData({ pdfBase64: base64 });
      const payload = resp?.data?.result ?? resp?.data ?? {};
      const extracted = payload.extractedData || {};

      const nameParts = (extracted.fullName || '').trim().split(/\s+/);
      const employmentMap = {
        'employed': 'employed', 'full-time': 'employed', 'full time': 'employed',
        'self-employed': 'self-employed', 'self employed': 'self-employed',
        'part-time': 'part-time', 'part time': 'part-time',
        'unemployed': 'unemployed', 'retired': 'retired', 'student': 'student',
      };
      const smokingMap = {
        'non-smoker': 'non-smoker', 'non smoker': 'non-smoker', 'nonsmoker': 'non-smoker', 'no': 'non-smoker',
        'smoker': 'smoker', 'yes': 'smoker', 'outside-only': 'outside-only',
      };

      setFormData(prev => ({
        ...prev,
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: extracted.email || '',
        phone: extracted.phone || '',
        monthlyIncome: normalizeNumericForNumberInput(extracted.monthlyIncome),
        creditScore: normalizeNumericForNumberInput(extracted.creditScore),
        currentEmployer: extracted.currentEmployer || '',
        employmentStatus: employmentMap[(extracted.employmentStatus || '').toLowerCase()] || '',
        proposedMoveInDate: normalizeDateForDateInput(extracted.proposedMoveInDate),
        numberOfOccupants: normalizeNumericForNumberInput(extracted.numberOfOccupants),
        hasPets: extracted.hasPets ?? null,
        smokingStatus: smokingMap[(extracted.smokingStatus || '').toLowerCase()] || '',
        priorEvictions: extracted.priorEvictions ?? null,
        additionalNotes: extracted.additionalNotes || '',
      }));
    } catch (err) {
      console.error('PDF extraction error:', err);
      const msg =
        err?.message ||
        err?.details ||
        'Could not extract data from this PDF. You can fill the form manually.';
      setExtractionError(typeof msg === 'string' ? msg : 'PDF extraction failed. You can fill the form manually.');
    } finally {
      const elapsed = Date.now() - extractSessionStartRef.current;
      const wait = Math.max(0, EXTRACT_BAR_MS - elapsed);
      extractCloseTimerRef.current = setTimeout(() => {
        extractCloseTimerRef.current = null;
        setExtractProgress(100);
        setExtractUiOpen(false);
      }, wait);
    }
  };

  const handleSubmit = async () => {
    if (!selectedOwnerId) {
      alert('Please select or enter a property owner.');
      return;
    }

    // Validate email format
    const isEmail = selectedOwnerId.includes('@');
    if (isEmail && !selectedOwnerId.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      alert('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    const applicantId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const extractedData = {
      fullName: `${formData.firstName} ${formData.lastName}`.trim(),
      email: formData.email || null,
      phone: formData.phone || null,
      monthlyIncome: formData.monthlyIncome ? parseFloat(formData.monthlyIncome) : null,
      creditScore: formData.creditScore ? parseInt(formData.creditScore) : null,
      currentEmployer: formData.currentEmployer || null,
      employmentStatus: formData.employmentStatus || null,
      proposedMoveInDate: formData.proposedMoveInDate || null,
      numberOfOccupants: formData.numberOfOccupants ? parseInt(formData.numberOfOccupants) : null,
      hasPets: formData.hasPets,
      smokingStatus: formData.smokingStatus || null,
      priorEvictions: formData.priorEvictions,
      references: formData.additionalNotes || null,
    };
    const hasFile = !!file;
    const status = 'pending_review';
    const selectedOwner = registeredOwners.find((owner) => owner.ownerUid === selectedOwnerId);

    try {
      await setDoc(doc(db, 'applicants', applicantId), {
        realtorId,
        ownerId: isEmail ? null : selectedOwnerId,
        ownerEmail: isEmail
          ? String(selectedOwnerId).trim().toLowerCase()
          : (selectedOwner?.ownerEmail ? String(selectedOwner.ownerEmail).trim().toLowerCase() : null),
        realtorEmail: realtorEmail || 'realtor@tenantdispatch.com',
        status,
        geminiProcessed: false,
        extractedData,
        manualEntry: true,
        additionalFiles: [],
        createdAt: serverTimestamp(),
      });
      if (hasFile) {
        const uploadTask = uploadBytesResumable(ref(storage, `applications/${realtorId}/${applicantId}.pdf`), file);
        await new Promise((resolve, reject) => {
          uploadTask.on('state_changed',
            (snap) => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
            reject, resolve
          );
        });
      }

      if (additionalFiles.length > 0) {
        const uploaded = [];
        for (const f of additionalFiles) {
          if (!f) continue;
          const safeName = String(f.name || 'file').replace(/[^\w.\-() ]+/g, '_').slice(0, 140);
          const storagePath = `applications/${realtorId}/${applicantId}/attachments/${Date.now()}_${safeName}`;
          const uploadTask = uploadBytesResumable(ref(storage, storagePath), f);
          setAdditionalUploadStatus({ name: f.name || safeName, progress: 0 });
          await new Promise((resolve, reject) => {
            uploadTask.on(
              'state_changed',
              (snap) => {
                setAdditionalUploadStatus({
                  name: f.name || safeName,
                  progress: Math.round((snap.bytesTransferred / snap.totalBytes) * 100),
                });
              },
              reject,
              resolve
            );
          });
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
          uploaded.push({
            name: f.name || safeName,
            contentType: f.type || null,
            size: typeof f.size === 'number' ? f.size : null,
            storagePath,
            downloadUrl,
            uploadedAt: new Date().toISOString(),
          });
        }
        await updateDoc(doc(db, 'applicants', applicantId), {
          additionalFiles: uploaded,
        });
        setAdditionalUploadStatus({ name: '', progress: 0 });
      }

      setFormData(INITIAL_FORM);
      setFile(null);
      setAdditionalFiles([]);
      setProgress(0);
      if (onUploaded) onUploaded(applicantId);
    } catch (err) {
      console.error(err);
      alert('Failed to create application: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const labelStyle = { display: 'block', fontWeight: 600, color: '#374151', marginBottom: 8, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' };
  const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', background: '#f9fafb', color: '#1f2937' };

  const extractionModal =
    extractUiOpen &&
    typeof document !== 'undefined' &&
    createPortal(
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10000,
          background: 'rgba(15, 23, 42, 0.55)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
        aria-hidden={false}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="realtor-extract-title"
          aria-busy="true"
          style={{
            background: '#fff',
            borderRadius: 16,
            padding: '28px 32px',
            maxWidth: 440,
            width: '100%',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            border: '1px solid #e5e7eb',
          }}
        >
          <h3 id="realtor-extract-title" style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800, color: '#111827' }}>
            Extracting your PDF
          </h3>
          <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: 14, lineHeight: 1.55 }}>
            {file?.name ? (
              <>
                Reading <strong style={{ color: '#374151' }}>{file.name}</strong>. Please keep this tab open.
              </>
            ) : (
              'Reading your application. Please keep this tab open.'
            )}
          </p>
          <div
            style={{
              height: 10,
              background: '#e0e7ff',
              borderRadius: 999,
              overflow: 'hidden',
              marginBottom: 6,
            }}
          >
            <div
              style={{
                width: `${extractProgress}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #4f46e5, #6366f1)',
                borderRadius: 999,
                transition: 'width 0.05s linear',
              }}
            />
          </div>
          <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 600, color: '#6366f1' }}>
            {Math.round(extractProgress)}%
          </p>
          <p style={{ margin: 0, fontSize: 12, color: '#9ca3af', lineHeight: 1.45 }}>
            The form stays paused until the timer completes. You can review and edit every field afterward.
          </p>
        </div>
      </div>,
      document.body
    );

  return (
    <div style={{ position: 'relative' }}>
      {extractionModal}
      <div
        style={{
          opacity: extractUiOpen ? 0.5 : 1,
          pointerEvents: extractUiOpen ? 'none' : 'auto',
          transition: 'opacity 0.2s ease',
          userSelect: extractUiOpen ? 'none' : 'auto',
        }}
        aria-hidden={extractUiOpen}
      >
      {/* PDF Attachment (optional) */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ ...labelStyle, textTransform: 'uppercase', fontSize: 13, letterSpacing: '0.05em' }}>Rental Application PDF <span style={{ color: '#9ca3af' }}>(optional — AI will extract & verify data)</span></label>
        {file ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 14px' }}>
            <span style={{ color: '#16a34a', fontWeight: 600, fontSize: 14, flex: 1 }}>📄 {file.name}</span>
            <button
              type="button"
              disabled={extractUiOpen}
              onClick={() => { setFile(null); setFormData(INITIAL_FORM); setExtractionError(''); }}
              style={{
                background: 'none',
                border: 'none',
                color: '#6b7280',
                cursor: extractUiOpen ? 'not-allowed' : 'pointer',
                fontSize: 18,
                lineHeight: 1,
                opacity: extractUiOpen ? 0.35 : 1,
              }}
              aria-label="Remove PDF"
            >
              ×
            </button>
          </div>
        ) : (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); if (!extractUiOpen) handleFile(e.dataTransfer.files[0]); }}
            onClick={() => { if (!extractUiOpen) fileInputRef.current?.click(); }}
            style={{
              border: `2px dashed ${dragging ? '#6366f1' : '#c7d2fe'}`,
              borderRadius: 10, padding: '16px 20px', textAlign: 'center', cursor: extractUiOpen ? 'not-allowed' : 'pointer',
              background: dragging ? '#eef2ff' : '#f8f9ff', color: '#6366f1', fontWeight: 600, fontSize: 14,
              opacity: extractUiOpen ? 0.6 : 1,
            }}
          >
            <input ref={fileInputRef} type="file" accept=".pdf" style={{ display: 'none' }} disabled={extractUiOpen} onChange={(e) => handleFile(e.target.files[0])} />
            {dragging ? 'Drop PDF here' : '📎 Attach PDF (drag & drop or click)'}
          </div>
        )}
        {extractionError && (
          <p style={{ margin: '10px 0 0', fontSize: 13, color: '#b91c1c', lineHeight: 1.4 }}>
            {extractionError}
          </p>
        )}
      </div>

      {/* Owner Selection */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Send Application to Owner *</label>
        {registeredOwners && registeredOwners.length > 0 ? (
          <div style={{ display: 'flex', gap: 10 }}>
            <select
              value={selectedOwnerId}
              onChange={(e) => setSelectedOwnerId(e.target.value)}
              style={{ ...inputStyle, background: '#fff', flex: 1 }}
            >
              <option value="">Select connected owner...</option>
              {registeredOwners.map((owner) => (
                <option key={owner.ownerUid} value={owner.ownerUid}>
                  {owner.ownerEmail}
                </option>
              ))}
            </select>
            <span style={{ padding: '10px 0', color: '#9ca3af', fontWeight: 500 }}>OR</span>
            <input
              type="email"
              value={selectedOwnerId && selectedOwnerId.includes('@') ? selectedOwnerId : ''}
              onChange={(e) => setSelectedOwnerId(e.target.value)}
              placeholder="new.owner@email.com"
              style={{ ...inputStyle, flex: 1 }}
            />
          </div>
        ) : (
          <input
            type="email"
            value={selectedOwnerId}
            onChange={(e) => setSelectedOwnerId(e.target.value)}
            placeholder="owner@email.com"
            style={inputStyle}
          />
        )}
      </div>

      {/* Tenant Selection */}
      {activeTenants && activeTenants.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>👥 Select Existing Tenant (Optional)</label>
          <select
            value={selectedTenant}
            onChange={(e) => {
              if (e.target.value) {
                handleTenantSelect(e.target.value);
              } else {
                setSelectedTenant('');
                setFormData(INITIAL_FORM);
              }
            }}
            style={{ ...inputStyle, background: '#fff' }}
          >
            <option value="">-- Select tenant to pre-fill form --</option>
            {activeTenants.map((tenant) => {
              const data = tenant.extractedData || {};
              return (
                <option key={tenant.id} value={tenant.id}>
                  {data.fullName || 'Unknown Tenant'} - {tenant.ownerDetails?.fullName || 'Unknown Owner'}
                </option>
              );
            })}
          </select>
          {selectedTenant && (
            <div style={{ 
              marginTop: 8, 
              padding: '8px 12px', 
              background: '#dcfce7', 
              border: '1px solid #86efac', 
              borderRadius: 6, 
              fontSize: 12, 
              color: '#166534' 
            }}>
              ✅ Form pre-filled with tenant information. You can modify any details before submitting.
            </div>
          )}
        </div>
      )}

      {/* Tenant Form */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={labelStyle}>First Name *</label>
          <input style={inputStyle} value={formData.firstName} onChange={(e) => updateField('firstName', e.target.value)} placeholder="First name" />
        </div>
        <div>
          <label style={labelStyle}>Last Name *</label>
          <input style={inputStyle} value={formData.lastName} onChange={(e) => updateField('lastName', e.target.value)} placeholder="Last name" />
        </div>
        <div>
          <label style={labelStyle}>Email</label>
          <input style={inputStyle} type="email" value={formData.email} onChange={(e) => updateField('email', e.target.value)} placeholder="jane@email.com" />
        </div>
        <div>
          <label style={labelStyle}>Phone</label>
          <input style={inputStyle} type="tel" value={formData.phone} onChange={(e) => updateField('phone', e.target.value)} placeholder="(555) 000-0000" />
        </div>
        <div>
          <label style={labelStyle}>Monthly Income ($)</label>
          <input style={inputStyle} type="number" value={formData.monthlyIncome} onChange={(e) => updateField('monthlyIncome', e.target.value)} placeholder="e.g. 5000" />
        </div>
        <div>
          <label style={labelStyle}>Credit Score</label>
          <input style={inputStyle} type="number" value={formData.creditScore} onChange={(e) => updateField('creditScore', e.target.value)} placeholder="e.g. 720" />
        </div>
        <div>
          <label style={labelStyle}>Current Employer</label>
          <input style={inputStyle} value={formData.currentEmployer} onChange={(e) => updateField('currentEmployer', e.target.value)} placeholder="Employer name" />
        </div>
        <div>
          <label style={labelStyle}>Employment Status</label>
          <select style={inputStyle} value={formData.employmentStatus} onChange={(e) => updateField('employmentStatus', e.target.value)}>
            <option value="">Select…</option>
            <option value="employed">Employed (Full-time)</option>
            <option value="self-employed">Self-Employed</option>
            <option value="part-time">Part-time</option>
            <option value="unemployed">Unemployed</option>
            <option value="retired">Retired</option>
            <option value="student">Student</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Proposed Move-in Date</label>
          <input style={inputStyle} type="date" value={formData.proposedMoveInDate} onChange={(e) => updateField('proposedMoveInDate', e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Number of Occupants</label>
          <input style={inputStyle} type="number" value={formData.numberOfOccupants} onChange={(e) => updateField('numberOfOccupants', e.target.value)} placeholder="e.g. 2" />
        </div>
        <div>
          <label style={labelStyle}>Pets</label>
          <select style={inputStyle} value={formData.hasPets === null ? '' : String(formData.hasPets)} onChange={(e) => updateField('hasPets', e.target.value === '' ? null : e.target.value === 'true')}>
            <option value="">Select…</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Smoking Status</label>
          <select style={inputStyle} value={formData.smokingStatus} onChange={(e) => updateField('smokingStatus', e.target.value)}>
            <option value="">Select…</option>
            <option value="non-smoker">Non-smoker</option>
            <option value="smoker">Smoker</option>
            <option value="outside-only">Outside Only</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Prior Evictions</label>
          <select style={inputStyle} value={formData.priorEvictions === null ? '' : String(formData.priorEvictions)} onChange={(e) => updateField('priorEvictions', e.target.value === '' ? null : e.target.value === 'true')}>
            <option value="">Select…</option>
            <option value="false">No</option>
            <option value="true">Yes</option>
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <label style={labelStyle}>Additional Notes / References</label>
        <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} value={formData.additionalNotes} onChange={(e) => updateField('additionalNotes', e.target.value)} placeholder="Any additional information…" />
      </div>

      {/* Additional supporting files (optional) */}
      <div style={{ marginBottom: 22 }}>
        <label style={labelStyle}>Additional Files (optional)</label>
        <div
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            padding: '12px 14px',
            background: '#fff',
          }}
        >
          <p style={{ margin: '0 0 10px 0', fontSize: 13, color: '#6b7280', lineHeight: 1.45 }}>
            Add supporting documents like pay stubs, ID images, pet records, or anything the owner requests. These upload with the application.
          </p>
          <input
            type="file"
            multiple
            disabled={submitting || extractUiOpen}
            onChange={(e) => {
              const next = Array.from(e.target.files || []);
              if (next.length === 0) return;
              setAdditionalFiles((prev) => {
                const merged = [...prev, ...next];
                return merged.slice(0, 10);
              });
              e.target.value = '';
            }}
          />

          {additionalFiles.length > 0 && (
            <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
              {additionalFiles.map((f, idx) => (
                <div
                  key={`${f.name}_${f.size}_${idx}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    padding: '8px 10px',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {f.name}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {(f.type || 'file').toLowerCase()} · {Math.round((f.size || 0) / 1024)} KB
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={submitting || extractUiOpen}
                    onClick={() => {
                      setAdditionalFiles((prev) => prev.filter((_, i) => i !== idx));
                    }}
                    style={{
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      padding: '6px 10px',
                      cursor: submitting || extractUiOpen ? 'not-allowed' : 'pointer',
                      color: '#111827',
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <div style={{ fontSize: 12, color: '#9ca3af' }}>
                {additionalFiles.length}/10 files selected
              </div>
            </div>
          )}

          {submitting && additionalUploadStatus?.name && (
            <div style={{ marginTop: 12 }}>
              <div style={{ background: '#e0e7ff', borderRadius: 999, height: 6, overflow: 'hidden' }}>
                <div style={{ width: `${additionalUploadStatus.progress || 0}%`, height: '100%', background: 'linear-gradient(90deg, #6366f1, #8b5cf6)', transition: 'width 0.2s' }} />
              </div>
              <p style={{ color: '#6366f1', fontSize: 12, marginTop: 6 }}>
                Uploading {additionalUploadStatus.name}… {additionalUploadStatus.progress || 0}%
              </p>
            </div>
          )}
        </div>
      </div>

      {progress > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ background: '#e0e7ff', borderRadius: 999, height: 6, overflow: 'hidden' }}>
            <div style={{ width: `${progress}%`, height: '100%', background: 'linear-gradient(90deg, #6366f1, #8b5cf6)', transition: 'width 0.3s' }} />
          </div>
          <p style={{ color: '#6366f1', fontSize: 12, marginTop: 4 }}>Uploading PDF… {progress}%</p>
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={submitting || extractUiOpen}
        style={{
          width: '100%', padding: '13px 0',
          background: submitting || extractUiOpen ? '#a5b4fc' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15,
          cursor: submitting || extractUiOpen ? 'not-allowed' : 'pointer', transition: 'opacity 0.2s',
        }}
      >
        {extractUiOpen ? '⚙️ Extracting PDF data…' : submitting ? '⚙️ Creating…' : '🚀 Create Application'}
      </button>
      </div>
    </div>
  );
}

// ─── Current placement (minimal tenant PII; owner is the ongoing client) ─────
function ActiveTenantRow({ tenant }) {
  const createdAt = tenant.createdAt?.toDate?.() || new Date();
  const convertedAt = tenant.convertedAt?.toDate?.() || tenant.updatedAt?.toDate?.() || createdAt;
  const unitLabel = tenant.unitNumber || tenant.unitDetails?.unitNumber || '—';
  const rent = tenant.unitDetails?.monthlyRent || tenant.tenantDetails?.monthlyRent;

  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '20px 24px',
      border: '1px solid #e5e7eb', marginBottom: 16,
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h3 style={{ margin: '0 0 6px 0', color: '#111827', fontSize: 17, fontWeight: 700 }}>
            Placement · Unit {unitLabel}
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <StatusBadge status={tenant.status} />
            <span style={{ color: '#6b7280', fontSize: 12 }}>
              Converted {convertedAt.toLocaleDateString()}
            </span>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 22, marginBottom: 4 }}>🏠</div>
          <div style={{ color: '#6b7280', fontSize: 11, fontWeight: 600 }}>CURRENT PLACEMENT</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 14 }}>
        <div>
          <h4 style={{ margin: '0 0 6px 0', color: '#374151', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Property owner
          </h4>
          <p style={{ margin: 0, color: '#111827', fontSize: 14, fontWeight: 600 }}>
            {tenant.ownerDetails?.fullName || '—'}
          </p>
          {tenant.ownerDetails?.email && (
            <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: 13 }}>{tenant.ownerDetails.email}</p>
          )}
        </div>
        <div>
          <h4 style={{ margin: '0 0 6px 0', color: '#374151', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Unit / lease (summary)
          </h4>
          {tenant.unitDetails?.address && (
            <p style={{ margin: '0 0 4px 0', color: '#374151', fontSize: 13 }}>{tenant.unitDetails.address}</p>
          )}
          {rent != null && rent !== '' && (
            <p style={{ margin: 0, color: '#111827', fontSize: 13, fontWeight: 600 }}>${rent}/mo</p>
          )}
          {(tenant.unitDetails?.leaseEnd || tenant.tenantDetails?.leaseEnd) && (
            <p style={{ margin: '6px 0 0 0', color: '#6b7280', fontSize: 12 }}>
              Lease ends {new Date(tenant.unitDetails?.leaseEnd || tenant.tenantDetails?.leaseEnd).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>

      <p style={{ margin: '0 0 12px 0', fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
        Tenant contact and screening details stay with the property owner after move-in. Use “Create new application” only when you are submitting a new packet (e.g. renewal or next lease).
      </p>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: 12,
        borderTop: '1px solid #f3f4f6',
        fontSize: 11,
        color: '#9ca3af',
        flexWrap: 'wrap',
        gap: 8,
      }}>
        <span>Originally submitted {createdAt.toLocaleDateString()}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>Application ID: {tenant.id?.slice(0, 12)}…</span>
          <button
            type="button"
            onClick={() => {
              if (window.handleCreateApplicationForTenant) {
                window.handleCreateApplicationForTenant(tenant);
              }
            }}
            style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              color: '#fff',
              border: 'none',
              padding: '6px 12px',
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            📝 Create new application
          </button>
        </div>
      </div>
    </div>
  );
}

function PastPlacementRow({ tenant }) {
  const ended = tenant.placementEndedAt?.toDate?.();
  const unitLabel = tenant.unitNumber || tenant.unitDetails?.unitNumber || '—';

  return (
    <div style={{
      background: '#f9fafb', borderRadius: 12, padding: '16px 20px',
      border: '1px solid #e5e7eb', marginBottom: 12,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <p style={{ margin: 0, fontWeight: 700, color: '#374151', fontSize: 15 }}>
            Past placement · Unit {unitLabel}
          </p>
          <p style={{ margin: '6px 0 0 0', fontSize: 13, color: '#6b7280' }}>
            {tenant.ownerDetails?.fullName || 'Property owner'}
            {tenant.ownerDetails?.email ? ` · ${tenant.ownerDetails.email}` : ''}
          </p>
        </div>
        <span style={{
          background: '#e5e7eb', color: '#4b5563',
          padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600,
        }}>
          Ended{ended ? ` ${ended.toLocaleDateString()}` : ''}
        </span>
      </div>
      <p style={{ margin: '10px 0 0 0', fontSize: 11, color: '#9ca3af' }}>Application ID: {tenant.id?.slice(0, 12)}…</p>
    </div>
  );
}

// ─── Active Owner Row Card ─────────────────────────────────────────────────────
function ActiveOwnerRow({ relationship }) {
  const acceptedAt = relationship.acceptedAt?.toDate?.() || new Date();

  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '20px 24px',
      border: '1px solid #e5e7eb', marginBottom: 16,
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    }}>
      {/* Header with owner name and status */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: '0 0 4px 0', color: '#111827', fontSize: 18, fontWeight: 700 }}>
            {relationship.ownerDetails?.fullName || relationship.ownerDetails?.email || relationship.ownerEmail || 'Owner'}
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{
              background: '#d1fae5', color: '#065f46',
              padding: '4px 10px', borderRadius: '999px', fontSize: 12, fontWeight: 600,
            }}>
              ● Connected
            </span>
            <span style={{ color: '#6b7280', fontSize: 12 }}>
              Connected on {acceptedAt.toLocaleDateString()}
            </span>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 24, marginBottom: 4 }}>🏢</div>
          <div style={{ color: '#6b7280', fontSize: 12, fontWeight: 600 }}>ACTIVE OWNER</div>
        </div>
      </div>

      {/* Owner Information Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 16 }}>
        {/* Contact Information */}
        <div>
          <h4 style={{ margin: '0 0 8px 0', color: '#374151', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            📞 Contact Information
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {relationship.ownerDetails?.email && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: '#6b7280', fontSize: 12 }}>Email:</span>
                <span style={{ color: '#111827', fontSize: 13, fontWeight: 600 }}>
                  {relationship.ownerDetails.email}
                </span>
              </div>
            )}
            {relationship.ownerDetails?.phone && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: '#6b7280', fontSize: 12 }}>Phone:</span>
                <span style={{ color: '#111827', fontSize: 13, fontWeight: 600 }}>
                  {relationship.ownerDetails.phone}
                </span>
              </div>
            )}
            {relationship.ownerDetails?.address && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: '#6b7280', fontSize: 12 }}>Address:</span>
                <span style={{ color: '#111827', fontSize: 13, fontWeight: 600 }}>
                  {relationship.ownerDetails.address}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Portfolio Summary */}
        <div>
          <h4 style={{ margin: '0 0 8px 0', color: '#374151', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            📊 Portfolio Summary
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#6b7280', fontSize: 12 }}>Current placements:</span>
              <span style={{ color: '#111827', fontSize: 13, fontWeight: 600 }}>
                {relationship.tenantCount} {relationship.tenantCount === 1 ? 'placement' : 'placements'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#6b7280', fontSize: 12 }}>Status:</span>
              <span style={{
                color: relationship.vacantUnitCount > 0 ? '#059669' : '#9ca3af',
                fontSize: 13,
                fontWeight: 600
              }}>
                {relationship.vacantUnitCount > 0
                  ? `${relationship.vacantUnitCount} vacant ${relationship.vacantUnitCount === 1 ? 'unit' : 'units'} - Actively accepting applications`
                  : (relationship.tenantCount || 0) === 0
                    ? 'No tenant placements yet — you can still submit applications for this owner'
                    : 'All units occupied'}
              </span>
            </div>
          </div>
        </div>

        {/* Relationship Details */}
        <div>
          <h4 style={{ margin: '0 0 8px 0', color: '#374151', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            🤝 Relationship Details
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#6b7280', fontSize: 12 }}>Invited:</span>
              <span style={{ color: '#111827', fontSize: 13, fontWeight: 600 }}>
                {relationship.ownerEmail}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#6b7280', fontSize: 12 }}>Realtor:</span>
              <span style={{ color: '#111827', fontSize: 13, fontWeight: 600 }}>
                {relationship.realtorName || '—'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer with relationship info */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        paddingTop: 12,
        borderTop: '1px solid #f3f4f6',
        fontSize: 11,
        color: '#9ca3af'
      }}>
        <span>Ready to receive new applications</span>
        <span>Relationship ID: {relationship.id?.slice(0, 12)}…</span>
      </div>
    </div>
  );
}

// ─── Applicant Row Card ────────────────────────────────────────────────────────
function ApplicantRow({ applicant, id, onCancel, canceling }) {
  const data = applicant.extractedData || {};
  const createdAt = applicant.createdAt?.toDate?.() || new Date();
  const canCancel = ['pending', 'pending_review', 'rejected', 'extraction_failed'].includes(applicant.status);

  return (
    <div
      data-applicant-id={id}
      style={{
      background: '#fff', borderRadius: 12, padding: '18px 22px',
      border: '1px solid #e5e7eb', marginBottom: 12,
      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
      display: 'grid',
      gridTemplateColumns: '1fr auto auto',
      alignItems: 'center', gap: 16,
    }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ fontWeight: 700, color: '#111827', fontSize: 15 }}>
            {data.fullName || 'Extracting…'}
          </span>
          <StatusBadge status={applicant.status} />
        </div>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {data.currentEmployer && (
            <span style={{ color: '#6b7280', fontSize: 13 }}>🏢 {data.currentEmployer}</span>
          )}
          {data.monthlyIncome > 0 && (
            <span style={{ color: '#6b7280', fontSize: 13 }}>
              💵 ${data.monthlyIncome?.toLocaleString()}/mo
            </span>
          )}
          {data.creditScore > 0 && (
            <span style={{ color: '#6b7280', fontSize: 13 }}>📊 {data.creditScore} credit score</span>
          )}
          {data.proposedMoveInDate && (
            <span style={{ color: '#6b7280', fontSize: 13 }}>📅 Move-in: {data.proposedMoveInDate}</span>
          )}
        </div>
        <p style={{ color: '#9ca3af', fontSize: 11, marginTop: 6 }}>
          Submitted {createdAt.toLocaleDateString()} · ID: {id?.slice(0, 12)}…
        </p>
        {applicant.ownerEmail && (
          <p style={{ color: '#6b7280', fontSize: 12, marginTop: 4 }}>
            Sent to owner: {applicant.ownerEmail}
          </p>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {applicant.pdfUrl ? (
          <a
            href={applicant.pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#f3f4f6', color: '#374151',
              padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              textDecoration: 'none', border: '1px solid #e5e7eb',
            }}
          >
            📄 View PDF
          </a>
        ) : (
          <span style={{ color: '#d1d5db', fontSize: 13 }}>Processing…</span>
        )}
        {canCancel && (
          <button
            type="button"
            onClick={() => onCancel(id)}
            disabled={canceling}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: canceling ? '#e5e7eb' : '#fff1f2',
              color: canceling ? '#9ca3af' : '#be123c',
              padding: '8px 14px',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 700,
              border: '1px solid ' + (canceling ? '#d1d5db' : '#fecdd3'),
              cursor: canceling ? 'not-allowed' : 'pointer'
            }}
          >
            {canceling ? 'Canceling...' : 'Cancel Application'}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Header Component ──────────────────────────────────────────────────────────
function RealtorHeader({ userData, user, onLogout }) {
  return (
    <div style={{
      background: '#1e293b', color: '#fff', padding: '14px 24px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      borderBottom: '1px solid #334155'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 24 }}>🏠</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Tenant Dispatch</div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, background: '#334155', padding: '3px 8px', borderRadius: 12, display: 'inline-block' }}>REALTOR</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
        <span style={{ fontSize: 14, color: '#e2e8f0' }}>{userData?.fullName || user?.email || 'User'}</span>
        <button
          onClick={onLogout}
          style={{
            background: '#334155', color: '#e2e8f0', border: 'none',
            padding: '8px 16px', borderRadius: 6, fontSize: 14, fontWeight: 600,
            cursor: 'pointer', transition: 'background 0.2s'
          }}
          onMouseOver={(e) => e.target.style.background = '#475569'}
          onMouseOut={(e) => e.target.style.background = '#334155'}
        >
          Sign Out
        </button>
      </div>
    </div>
  );
}

// ─── Invite Owner Modal/Section ────────────────────────────────────────────────
function InviteOwnerTab({ realtorId, realtorName, userData }) {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [loadingInvitations, setLoadingInvitations] = useState(true);
  const [invitationLoadError, setInvitationLoadError] = useState('');
  const [deletingInvitationId, setDeletingInvitationId] = useState(null);
  const [selectedInvitationIds, setSelectedInvitationIds] = useState(() => new Set());
  const [bulkInviteDeleteLoading, setBulkInviteDeleteLoading] = useState(false);
  const [bulkInviteActionMessage, setBulkInviteActionMessage] = useState('');

  // Listen to invitations for this realtor
  useEffect(() => {
    if (!realtorId) return;

    const q = query(collection(db, 'invitations'), where('realtorId', '==', realtorId));
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Filter out connected/registered owners - they should appear in Active Owners tab
      const pendingInvitations = docs.filter(inv => inv.status !== 'connected' && inv.status !== 'registered');
      setInvitations(pendingInvitations);
      setLoadingInvitations(false);
    }, (err) => {
      console.error('Invitations listener error:', err);
      setInvitationLoadError(err?.message || 'Failed to load invitations');
      setLoadingInvitations(false);
    });

    return () => unsub();
  }, [realtorId]);

  // Drop selections for invites that disappeared from the list
  useEffect(() => {
    const valid = new Set(invitations.map((inv) => inv.id));
    setSelectedInvitationIds((prev) => {
      const next = new Set([...prev].filter((id) => valid.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [invitations]);

  const handleInvite = async () => {
    setError('');
    setMessage('');

    if (!email) {
      setError('Please enter an email address');
      return;
    }

    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const sendInvite = httpsCallable(functions, 'sendOwnerInvite');
      const response = await sendInvite({
        ownerEmail: email,
        realtorName: realtorName || userData?.fullName || 'A realtor',
      });

      if (response.data.success) {
        setMessage('✅ Invite sent successfully!');
        setEmail('');
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (err) {
      console.error('Invite error:', err);
      setError('Failed to send invite: ' + (err.message || 'Please try again'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInvitation = async (invitation) => {
    if (!invitation?.id) return;
    setError('');
    setMessage('');

    const ownerEmail = invitation.ownerEmail || 'this owner';
    const confirmed = window.confirm(
      `Delete this invitation for ${ownerEmail}?\n\nThis will remove it from your invited list. You can always re-invite later.`
    );
    if (!confirmed) return;

    try {
      setDeletingInvitationId(invitation.id);
      const fn = httpsCallable(functions, 'deleteOwnerInvitations');
      const res = await fn({ invitationIds: [invitation.id] });
      if (res.data?.deleted === 0) {
        setError('That invitation was already removed.');
      } else {
        setMessage('🗑️ Invitation deleted.');
        setTimeout(() => setMessage(''), 4000);
      }
    } catch (err) {
      console.error('Delete invitation error:', err);
      setError('Failed to delete invitation: ' + (err.message || 'Please try again'));
    } finally {
      setDeletingInvitationId(null);
    }
  };

  const toggleInvitationSelection = (id) => {
    setSelectedInvitationIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllInvitations = () => {
    if (selectedInvitationIds.size === invitations.length && invitations.length > 0) {
      setSelectedInvitationIds(new Set());
    } else {
      setSelectedInvitationIds(new Set(invitations.map((inv) => inv.id)));
    }
  };

  const bulkDeleteInvitations = async () => {
    if (selectedInvitationIds.size === 0) return;
    const count = selectedInvitationIds.size;
    const confirmed = window.confirm(
      `Delete ${count} invitation${count > 1 ? 's' : ''}?\n\nThis cannot be undone. You can send new invites later.`
    );
    if (!confirmed) return;

    setBulkInviteDeleteLoading(true);
    setBulkInviteActionMessage('');
    setError('');
    setMessage('');

    const ids = [...selectedInvitationIds];

    try {
      const fn = httpsCallable(functions, 'deleteOwnerInvitations');
      const res = await fn({ invitationIds: ids });
      const deleted = typeof res.data?.deleted === 'number' ? res.data.deleted : 0;
      if (deleted === 0) {
        setBulkInviteActionMessage('No matching invitations found (they may have already been removed).');
      } else {
        setBulkInviteActionMessage(
          `✓ Deleted ${deleted} invitation${deleted > 1 ? 's' : ''}.`
        );
      }
      setSelectedInvitationIds(new Set());
      setTimeout(() => setBulkInviteActionMessage(''), 5000);
    } catch (err) {
      console.error('Bulk delete invitations error:', err);
      setBulkInviteActionMessage(`❌ ${err.message || 'Could not delete invitations.'}`);
      setTimeout(() => setBulkInviteActionMessage(''), 8000);
    } finally {
      setBulkInviteDeleteLoading(false);
    }
  };

  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: 32, border: '1px solid #e5e7eb', boxShadow: '0 1px 8px rgba(0,0,0,0.05)' }}>
      <h2 style={{ margin: '0 0 6px 0', color: '#111827', fontSize: 20, fontWeight: 700 }}>Invite Property Owner</h2>
      <p style={{ color: '#9ca3af', fontSize: 14, marginBottom: 28 }}>
        Invite a property owner to join Tenant Dispatch so they can review tenant applications.
      </p>

      {message && (
        <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 10, padding: '14px 18px', marginBottom: 24 }}>
          <p style={{ color: '#065f46', fontWeight: 700, margin: 0 }}>{message}</p>
        </div>
      )}

      {error && (
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 10, padding: '14px 18px', marginBottom: 24 }}>
          <p style={{ color: '#991b1b', fontWeight: 700, margin: 0 }}>{error}</p>
        </div>
      )}

      {invitationLoadError && (
        <div style={{ background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 10, padding: '14px 18px', marginBottom: 24 }}>
          <p style={{ color: '#9a3412', fontWeight: 700, margin: 0 }}>{invitationLoadError}</p>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <div>
          <label style={{ display: 'block', fontWeight: 600, color: '#374151', marginBottom: 8, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Owner Email *</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="owner@property.com"
            style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', background: '#f9fafb', color: '#1f2937' }}
          />
        </div>
      </div>

      <button
        onClick={handleInvite}
        disabled={loading}
        style={{
          width: '100%',
          padding: '13px 0',
          background: loading ? '#a5b4fc' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15,
          cursor: loading ? 'not-allowed' : 'pointer', transition: 'opacity 0.2s',
        }}
      >
        {loading ? '⚙️ Sending…' : '📧 Send Invite'}
      </button>

      {/* Invited Owners List */}
      <div style={{ marginTop: 40 }}>
        <h3 style={{ margin: '0 0 20px 0', color: '#111827', fontSize: 18, fontWeight: 700 }}>Invited Property Owners</h3>
        {loadingInvitations ? (
          <p style={{ color: '#9ca3af' }}>Loading invitations...</p>
        ) : invitations.length === 0 ? (
          <p style={{ color: '#9ca3af', fontSize: 14 }}>No invitations sent yet.</p>
        ) : (
          <>
            {selectedInvitationIds.size > 0 && (
              <div
                style={{
                  marginBottom: 16,
                  padding: '14px 18px',
                  background: '#eef2ff',
                  border: '1px solid #a5b4fc',
                  borderRadius: 10,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 12,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                  <span style={{ fontWeight: 700, color: '#3730a3', fontSize: 14 }}>
                    {selectedInvitationIds.size} selected
                  </span>
                  <button
                    type="button"
                    onClick={bulkDeleteInvitations}
                    disabled={bulkInviteDeleteLoading}
                    style={{
                      padding: '8px 14px',
                      background: bulkInviteDeleteLoading ? '#fca5a5' : '#ef4444',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: bulkInviteDeleteLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {bulkInviteDeleteLoading ? 'Deleting…' : 'Delete selected'}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedInvitationIds(new Set())}
                  disabled={bulkInviteDeleteLoading}
                  style={{
                    padding: '8px 14px',
                    background: '#e5e7eb',
                    color: '#374151',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: bulkInviteDeleteLoading ? 'not-allowed' : 'pointer',
                  }}
                >
                  Clear selection
                </button>
                {bulkInviteActionMessage ? (
                  <div
                    style={{
                      width: '100%',
                      marginTop: 4,
                      padding: '10px 12px',
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 600,
                      background: bulkInviteActionMessage.startsWith('✓') ? '#f0fdf4' : '#fef2f2',
                      color: bulkInviteActionMessage.startsWith('✓') ? '#166534' : '#991b1b',
                      border: `1px solid ${bulkInviteActionMessage.startsWith('✓') ? '#86efac' : '#fecaca'}`,
                    }}
                  >
                    {bulkInviteActionMessage}
                  </div>
                ) : null}
              </div>
            )}

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 12,
                padding: '10px 12px',
                background: '#f9fafb',
                borderRadius: 8,
                border: '1px solid #e5e7eb',
              }}
            >
              <input
                type="checkbox"
                checked={invitations.length > 0 && selectedInvitationIds.size === invitations.length}
                onChange={toggleSelectAllInvitations}
                style={{ width: 18, height: 18, cursor: 'pointer' }}
                aria-label="Select all invitations"
              />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#4b5563' }}>Select all</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {invitations.map((invitation) => {
                const statusBadgeConfig = {
                  pending_signup: { bg: '#fef3c7', color: '#92400e', label: 'Waiting For Signup', dot: '#f59e0b' },
                  pending_owner_acceptance: { bg: '#dbeafe', color: '#1d4ed8', label: 'Awaiting Owner Approval', dot: '#3b82f6' },
                  connected: { bg: '#d1fae5', color: '#065f46', label: 'Connected', dot: '#10b981' },
                  registered: { bg: '#dbeafe', color: '#1d4ed8', label: 'Awaiting Owner Approval', dot: '#3b82f6' },
                  rejected: { bg: '#fee2e2', color: '#991b1b', label: 'Rejected', dot: '#ef4444' },
                };
                const cfg = statusBadgeConfig[invitation.status] || statusBadgeConfig.pending_signup;
                const sentDate = invitation.sentAt?.toDate?.() || new Date();
                const isDeleting = deletingInvitationId === invitation.id;
                const isSelected = selectedInvitationIds.has(invitation.id);
                return (
                  <div
                    key={invitation.id}
                    style={{
                      background: isSelected ? '#eef2ff' : '#f9fafb',
                      border: `1px solid ${isSelected ? '#c7d2fe' : '#e5e7eb'}`,
                      borderRadius: 10,
                      padding: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 16,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, minWidth: 0, flex: 1 }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleInvitationSelection(invitation.id)}
                        disabled={bulkInviteDeleteLoading}
                        style={{ width: 18, height: 18, marginTop: 2, cursor: bulkInviteDeleteLoading ? 'not-allowed' : 'pointer', flexShrink: 0 }}
                        aria-label={`Select invitation ${invitation.ownerEmail || invitation.id}`}
                      />
                      <div style={{ minWidth: 0 }}>
                        <p style={{ margin: 0, color: '#1f2937', fontWeight: 600 }}>{invitation.ownerEmail}</p>
                        <p style={{ margin: '4px 0 0 0', color: '#9ca3af', fontSize: 12 }}>
                          Sent {sentDate.toLocaleDateString()} at {sentDate.toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          background: cfg.bg,
                          color: cfg.color,
                          padding: '4px 12px',
                          borderRadius: 999,
                          fontSize: 12,
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.dot }} />
                        {cfg.label}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleDeleteInvitation(invitation)}
                        disabled={isDeleting || bulkInviteDeleteLoading}
                        style={{
                          background: isDeleting ? '#e2e8f0' : '#fee2e2',
                          color: '#991b1b',
                          border: '1px solid #fecaca',
                          borderRadius: 10,
                          padding: '6px 10px',
                          fontSize: 12,
                          fontWeight: 800,
                          cursor: isDeleting || bulkInviteDeleteLoading ? 'not-allowed' : 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                        title="Delete invitation"
                      >
                        {isDeleting ? 'Deleting…' : 'Delete'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function RealtorSettingsTab({ user, userData }) {
  const [editingProfile, setEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [profileForm, setProfileForm] = useState({
    fullName: userData?.fullName || user?.displayName || '',
    email: userData?.email || user?.email || '',
    phoneNumber: userData?.phoneNumber || '',
    licenseNumber: userData?.licenseNumber || '',
    brokerageName: userData?.brokerageName || '',
  });

  useEffect(() => {
    setProfileForm({
      fullName: userData?.fullName || user?.displayName || '',
      email: userData?.email || user?.email || '',
      phoneNumber: userData?.phoneNumber || '',
      licenseNumber: userData?.licenseNumber || '',
      brokerageName: userData?.brokerageName || '',
    });
  }, [userData, user]);

  const updateField = (field, value) => {
    setProfileForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = async () => {
    if (!user?.uid) return;
    if (!profileForm.fullName.trim()) {
      setProfileMessage('Please add your name before saving.');
      return;
    }

    setSavingProfile(true);
    setProfileMessage('');

    try {
      await setDoc(doc(db, 'RealtorsData', user.uid), {
        role: 'realtor',
        email: (profileForm.email || user.email || '').trim().toLowerCase(),
        fullName: profileForm.fullName.trim(),
        phoneNumber: profileForm.phoneNumber.trim(),
        licenseNumber: profileForm.licenseNumber.trim(),
        brokerageName: profileForm.brokerageName.trim(),
        updatedAt: serverTimestamp(),
      }, { merge: true });

      setEditingProfile(false);
      setProfileMessage('Profile updated successfully.');
    } catch (error) {
      console.error('Error saving realtor profile:', error);
      setProfileMessage(error?.message || 'Could not save profile updates right now.');
    } finally {
      setSavingProfile(false);
    }
  };

  const cardStyle = {
    background: '#fff',
    borderRadius: 16,
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 8px rgba(0,0,0,0.05)',
    padding: 28,
  };

  const fieldLabelStyle = {
    display: 'block',
    fontWeight: 700,
    color: '#374151',
    marginBottom: 8,
    fontSize: 12,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  };

  const inputStyle = {
    width: '100%',
    padding: '11px 14px',
    border: '1px solid #d1d5db',
    borderRadius: 10,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
    background: '#f9fafb',
    color: '#111827',
  };

  return (
    <div>
      <SectionIntro
        eyebrow="Profile Settings"
        title="Keep your realtor details current"
        description="Update the information owners rely on when they receive invites and review submitted applications."
      />

      <DashboardAppearanceSettings style={{ marginBottom: 20 }} />

      {profileMessage && (
        <div style={{
          background: profileMessage.includes('successfully') ? '#d1fae5' : '#fee2e2',
          border: `1px solid ${profileMessage.includes('successfully') ? '#6ee7b7' : '#fca5a5'}`,
          borderRadius: 10,
          padding: '14px 18px',
          marginBottom: 20,
        }}>
          <p style={{ margin: 0, color: profileMessage.includes('successfully') ? '#065f46' : '#991b1b', fontWeight: 700 }}>
            {profileMessage}
          </p>
        </div>
      )}

      <div className="td-realtor-settings-profile-card" style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: '0 0 6px 0', color: '#111827', fontSize: 20, fontWeight: 700 }}>Realtor Profile</h3>
            <p style={{ margin: 0, color: '#6b7280', fontSize: 14 }}>
              This is the information Tenant Dispatch shows across invites, owner connections, and your internal account records.
            </p>
          </div>
          {!editingProfile && (
            <button
              onClick={() => {
                setEditingProfile(true);
                setProfileMessage('');
              }}
              style={{
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                padding: '10px 18px',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Edit Details
            </button>
          )}
        </div>

        {editingProfile ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 18 }}>
            <div>
              <label style={fieldLabelStyle}>Full Name</label>
              <input style={inputStyle} value={profileForm.fullName} onChange={(e) => updateField('fullName', e.target.value)} placeholder="Your full name" />
            </div>
            <div>
              <label style={fieldLabelStyle}>Email</label>
              <input style={inputStyle} value={profileForm.email} onChange={(e) => updateField('email', e.target.value)} placeholder="name@brokerage.com" type="email" />
            </div>
            <div>
              <label style={fieldLabelStyle}>Phone Number</label>
              <input style={inputStyle} value={profileForm.phoneNumber} onChange={(e) => updateField('phoneNumber', e.target.value)} placeholder="(555) 555-5555" />
            </div>
            <div>
              <label style={fieldLabelStyle}>Realtor License #</label>
              <input style={inputStyle} value={profileForm.licenseNumber} onChange={(e) => updateField('licenseNumber', e.target.value)} placeholder="License number" />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={fieldLabelStyle}>Brokerage Name</label>
              <input style={inputStyle} value={profileForm.brokerageName} onChange={(e) => updateField('brokerageName', e.target.value)} placeholder="Brokerage or team name" />
            </div>
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
              <button
                onClick={handleSaveProfile}
                disabled={savingProfile}
                style={{
                  background: savingProfile ? '#a5b4fc' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  padding: '11px 20px',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: savingProfile ? 'not-allowed' : 'pointer',
                }}
              >
                {savingProfile ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={() => {
                  setEditingProfile(false);
                  setProfileMessage('');
                  setProfileForm({
                    fullName: userData?.fullName || user?.displayName || '',
                    email: userData?.email || user?.email || '',
                    phoneNumber: userData?.phoneNumber || '',
                    licenseNumber: userData?.licenseNumber || '',
                    brokerageName: userData?.brokerageName || '',
                  });
                }}
                style={{
                  background: '#fff',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: 10,
                  padding: '11px 20px',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18 }}>
            {[
              ['Full Name', profileForm.fullName || user?.displayName || 'Not set'],
              ['Email', profileForm.email || user?.email || 'Not set'],
              ['Phone Number', profileForm.phoneNumber || 'Not set'],
              ['Realtor License #', profileForm.licenseNumber || 'Not set'],
              ['Brokerage Name', profileForm.brokerageName || 'Not set'],
            ].map(([label, value]) => (
              <div key={label} style={{ background: '#f8fafc', borderRadius: 12, padding: '16px 18px', border: '1px solid #e2e8f0' }}>
                <p style={{ margin: '0 0 6px 0', color: '#64748b', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {label}
                </p>
                <p style={{ margin: 0, color: '#111827', fontSize: 15, fontWeight: 600 }}>
                  {value}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function VacantUnitsPanel({ vacancies = [], onStartSubmitting }) {
  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: '0 0 6px 0', color: '#111827', fontSize: 20, fontWeight: 700 }}>Units Needing Applicants</h3>
          <p style={{ margin: 0, color: '#6b7280', fontSize: 14 }}>
            These connected owners have marked units as vacant and ready for you to start sourcing applicants.
          </p>
        </div>
        {vacancies.length > 0 && (
          <button
            onClick={onStartSubmitting}
            style={{
              background: '#111827',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              padding: '10px 16px',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Start A Submission
          </button>
        )}
      </div>

      {vacancies.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: 14, padding: '22px 24px', border: '1px solid #e5e7eb' }}>
          <p style={{ margin: 0, color: '#6b7280', fontSize: 14 }}>
            No connected owners have posted a vacant unit yet. Once they mark a unit as ready for applicants, it will appear here.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
          {vacancies.map((unit) => (
            <div key={unit.id} style={{ background: '#fff', borderRadius: 16, padding: '20px 22px', border: '1px solid #e5e7eb', boxShadow: '0 1px 6px rgba(15,23,42,0.05)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
                <div>
                  <p style={{ margin: '0 0 4px 0', color: '#111827', fontSize: 18, fontWeight: 800 }}>
                    Unit {unit.unitNumber || 'N/A'}
                  </p>
                  <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>
                    {unit.address || 'Address not provided'}
                  </p>
                </div>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: '#dcfce7',
                  color: '#166534',
                  padding: '4px 10px',
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e' }} />
                  Vacant
                </span>
              </div>

              <div style={{ display: 'grid', gap: 8, marginBottom: 14 }}>
                <p style={{ margin: 0, color: '#374151', fontSize: 14 }}>
                  <strong>Owner:</strong> {unit.ownerEmail || 'Connected owner'}
                </p>
                <p style={{ margin: 0, color: '#374151', fontSize: 14 }}>
                  <strong>Rent:</strong> ${Number(unit.monthlyRent || 0).toLocaleString()}
                </p>
                {unit.leaseStart && (
                  <p style={{ margin: 0, color: '#374151', fontSize: 14 }}>
                    <strong>Lease Start:</strong> {unit.leaseStart}
                  </p>
                )}
              </div>

              {unit.vacancyNotes && (
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
                  <p style={{ margin: '0 0 6px 0', color: '#475569', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Owner Notes
                  </p>
                  <p style={{ margin: 0, color: '#334155', fontSize: 13, lineHeight: 1.5 }}>
                    {unit.vacancyNotes}
                  </p>
                </div>
              )}

              <button
                onClick={onStartSubmitting}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  padding: '11px 0',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Submit Applicant
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main RealtorDashboard Component ──────────────────────────────────────────
export default function RealtorDashboard({ user, userData, onLogout }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [applicants, setApplicants] = useState([]);
  const [loadingApplicants, setLoadingApplicants] = useState(true);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cancelingApplicantId, setCancelingApplicantId] = useState(null);
  const [registeredOwners, setRegisteredOwners] = useState([]);
  const [registeredOwnersError, setRegisteredOwnersError] = useState('');
  const [vacantUnits, setVacantUnits] = useState([]);
  
  // New state for active tenants and owners
  const [activeTenants, setActiveTenants] = useState([]);
  const [pastPlacements, setPastPlacements] = useState([]);
  const [loadingActiveTenants, setLoadingActiveTenants] = useState(true);
  const [activeOwners, setActiveOwners] = useState([]);
  const [loadingActiveOwners, setLoadingActiveOwners] = useState(true);
  
  // Owner-first: landlords are the ongoing client; placements are derived from live unit/tenant data
  const [relationshipsView, setRelationshipsView] = useState('owners'); // 'owners' | 'placements' | 'past'
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form data for application creation
  const [formData, setFormData] = useState(INITIAL_FORM);
  
  const realtorFirstName = getDisplayName(userData?.fullName, user?.email);
  const greetingLabel = getGreetingLabel();

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db, 'applicants'), where('realtorId', '==', user.uid));
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      // Filter out converted tenants from the main applicants list
      setApplicants(docs.filter(doc => doc.status !== 'converted_to_tenant'));
      setLoadingApplicants(false);
    }, (error) => {
      console.error('Applicants listener error:', error);
      setLoadingApplicants(false);
    });
    return () => unsub();
  }, [user?.uid]);

  // Fetch owner connections that have been explicitly accepted
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db, 'invitations'), where('realtorId', '==', user.uid), where('status', '==', 'connected'));
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRegisteredOwners(docs);
      setRegisteredOwnersError('');
    }, (err) => {
      console.error('Registered owners listener error:', err);
      setRegisteredOwners([]);
      setRegisteredOwnersError(err?.code === 'permission-denied'
        ? 'Connected owners could not be loaded because invitations access is blocked.'
        : (err?.message || 'Unable to load connected owners.'));
    });
    return () => unsub();
  }, [user?.uid]);

  useEffect(() => {
    if (!registeredOwners.length) {
      setVacantUnits([]);
      return;
    }

    const ownerIds = [...new Set(registeredOwners.map((owner) => owner.ownerUid).filter(Boolean))];
    if (!ownerIds.length) {
      setVacantUnits([]);
      return;
    }

    const chunks = [];
    for (let i = 0; i < ownerIds.length; i += 10) {
      chunks.push(ownerIds.slice(i, i + 10));
    }

    const unsubscribes = chunks.map((chunk) => onSnapshot(
      query(collection(db, 'units'), where('ownerId', 'in', chunk)),
      (snap) => {
        setVacantUnits((prev) => {
          const nextMap = new Map(prev.map((unit) => [unit.id, unit]));
          const currentChunkIds = new Set(chunk);

          prev.forEach((unit) => {
            if (currentChunkIds.has(unit.ownerId)) {
              nextMap.delete(unit.id);
            }
          });

          snap.docs.forEach((docSnap) => {
            const unit = { id: docSnap.id, ...docSnap.data() };
            if (unit.readyForApplicants) {
              const ownerConnection = registeredOwners.find(
                (owner) => owner.ownerUid === unit.ownerId || owner.acceptedBy === unit.ownerId
              );
              nextMap.set(unit.id, {
                ...unit,
                ownerEmail: ownerConnection?.ownerEmail || '',
              });
            }
          });

          return Array.from(nextMap.values()).sort((a, b) => {
            const aTs = a.vacancyUpdatedAt?.seconds || a.createdAt?.seconds || 0;
            const bTs = b.vacancyUpdatedAt?.seconds || b.createdAt?.seconds || 0;
            return bTs - aTs;
          });
        });
      },
      (error) => {
        console.error('Vacant units listener error:', error);
      }
    ));

    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [registeredOwners]);

  // Fetch active tenants (converted applicants with tenant info)
  useEffect(() => {
    if (!user?.uid) return;
    
    const q = query(
      collection(db, 'applicants'), 
      where('realtorId', '==', user.uid),
      where('status', '==', 'converted_to_tenant')
    );
    
    const unsub = onSnapshot(q, async (snap) => {
      const tenantDocs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      
      // Fetch tenant details and unit data for each converted applicant
      const tenantsWithDetails = await Promise.all(
        tenantDocs.map(async (applicant) => {
          let tenantDetails = null;
          let ownerDetails = null;
          let unitDetails = null;
          
          // Fetch tenant details
          if (applicant.tenantId) {
            try {
              const tenantSnap = await getDoc(doc(db, 'TenantsData', applicant.tenantId));
              if (tenantSnap.exists()) {
                tenantDetails = { id: tenantSnap.id, ...tenantSnap.data() };
              }
            } catch (error) {
              console.error('Error fetching tenant details:', error);
            }
          }
          
          // Fetch owner details (from OwnersData, not RealtorsData)
          if (applicant.ownerId) {
            try {
              const ownerSnap = await getDoc(doc(db, 'OwnersData', applicant.ownerId));
              if (ownerSnap.exists()) {
                ownerDetails = { id: ownerSnap.id, ...ownerSnap.data() };
              }
            } catch (error) {
              console.error('Error fetching owner details:', error);
            }
          }
          
          // Fetch unit details for lease information
          if (applicant.unitId) {
            try {
              const unitSnap = await getDoc(doc(db, 'units', applicant.unitId));
              if (unitSnap.exists()) {
                unitDetails = { id: unitSnap.id, ...unitSnap.data() };
              }
            } catch (error) {
              console.error('Error fetching unit details:', error);
            }
          }
          
          return {
            ...applicant,
            tenantDetails,
            ownerDetails,
            unitDetails
          };
        })
      );
      
      const current = tenantsWithDetails.filter((row) => isCurrentPlacement(row, row.tenantDetails));
      const past = tenantsWithDetails.filter((row) => !isCurrentPlacement(row, row.tenantDetails));
      setActiveTenants(current);
      setPastPlacements(past);
      setLoadingActiveTenants(false);
    }, (error) => {
      console.error('Error fetching active tenants:', error);
      // Handle permission errors gracefully
      if (error.code === 'permission-denied') {
        console.log('TenantsData collection not accessible, using fallback');
        setActiveTenants([]);
        setPastPlacements([]);
      } else {
        console.error('Unexpected error:', error);
      }
      setLoadingActiveTenants(false);
    });
    
    return () => unsub();
  }, [user?.uid]);

  // Derive active owners from active tenants (proof of connection)
  useEffect(() => {
    setLoadingActiveOwners(true);

    // Build map of unique owners from tenants
    const ownerMap = new Map();

    activeTenants.forEach((tenant) => {
      if (tenant.ownerDetails && tenant.ownerDetails.id) {
        const ownerId = tenant.ownerDetails.id;

        if (!ownerMap.has(ownerId)) {
          ownerMap.set(ownerId, {
            id: ownerId,
            ownerId: ownerId,
            ownerDetails: tenant.ownerDetails,
            ownerEmail: tenant.ownerDetails?.email || '',
            acceptedAt: new Date(), // Derived owners don't have explicit acceptance, use now
            tenantCount: 0,
            tenants: [],
            totalUnits: 0,
            vacantUnitCount: 0
          });
        }

        const owner = ownerMap.get(ownerId);
        owner.tenantCount += 1;
        owner.tenants.push({
          tenantId: tenant.tenantDetails?.id,
          unitNumber: tenant.unitDetails?.unitNumber || tenant.unitNumber || 'N/A',
        });
      }
    });

    // Fetch units for each owner to count vacancies
    const fetchOwnerUnits = async () => {
      const updatedOwners = [];

      for (const [ownerId, owner] of ownerMap) {
        try {
          const unitsQ = query(
            collection(db, 'units'),
            where('ownerId', '==', ownerId)
          );
          const unitsSnap = await getDocs(unitsQ);
          const totalUnits = unitsSnap.docs.length;
          const vacantCount = Math.max(0, totalUnits - owner.tenantCount);

          updatedOwners.push({
            ...owner,
            totalUnits,
            vacantUnitCount: vacantCount
          });
        } catch (error) {
          console.error('Error fetching units for owner:', ownerId, error);
          updatedOwners.push({ ...owner, totalUnits: 0, vacantUnitCount: 0 });
        }
      }

      setActiveOwners(updatedOwners);
      setLoadingActiveOwners(false);
    };

    if (ownerMap.size > 0) {
      fetchOwnerUnits();
    } else {
      setActiveOwners([]);
      setLoadingActiveOwners(false);
    }
  }, [activeTenants]);

  /** Owners with live placements plus connected invites that have no placements yet (accepted invite). */
  const mergedConnectedOwners = useMemo(() => {
    const realtorDisplayName = userData?.fullName || user?.email || '';
    const rows = [...activeOwners];
    const seenOwnerId = new Set(activeOwners.map((o) => o.ownerId).filter(Boolean));
    const seenEmail = new Set(
      activeOwners
        .map((o) => (o.ownerDetails?.email || o.ownerEmail || '').toLowerCase())
        .filter(Boolean)
    );
    (registeredOwners || []).filter((i) => i.status === 'connected').forEach((inv) => {
      if (inv.acceptedBy && seenOwnerId.has(inv.acceptedBy)) return;
      const emailLc = (inv.ownerEmail || '').toLowerCase();
      if (emailLc && seenEmail.has(emailLc)) return;
      rows.push({
        id: `inv-${inv.id}`,
        ownerId: inv.acceptedBy || null,
        ownerDetails: {
          fullName: '',
          email: inv.ownerEmail || '',
          phone: '',
          address: '',
        },
        ownerEmail: inv.ownerEmail,
        realtorName: inv.realtorName || realtorDisplayName,
        acceptedAt: inv.acceptedAt || inv.sentAt,
        tenantCount: 0,
        vacantUnitCount: 0,
        totalUnits: 0,
        fromConnectedInvite: true,
      });
    });
    return rows;
  }, [activeOwners, registeredOwners, userData?.fullName, user?.email]);

  const pendingCount = applicants.filter((a) => ['pending', 'pending_review'].includes(a.status)).length;
  const approvedOnlyCount = applicants.filter((a) => a.status === 'approved').length;
  const ownersWithPlacementsCount = mergedConnectedOwners.filter((o) => (o.tenantCount || 0) > 0).length;
  const ownersNoPlacementsYet = Math.max(0, mergedConnectedOwners.length - ownersWithPlacementsCount);

  const nav = (tab) => { setActiveTab(tab); setMobileMenuOpen(false); };

  // Handle creating application for existing tenant
  const handleCreateApplicationForTenant = (tenant) => {
    const tenantData = tenant.extractedData || {};
    
    // Pre-fill form with tenant data
    const preFilledData = {
      firstName: tenantData.firstName || tenantData.fullName?.split(' ')[0] || '',
      lastName: tenantData.lastName || tenantData.fullName?.split(' ').slice(1).join(' ') || '',
      email: tenantData.email || '',
      phone: tenantData.phone || '',
      monthlyIncome: tenantData.monthlyIncome?.toString() || '',
      creditScore: tenantData.creditScore?.toString() || '',
      currentEmployer: tenantData.currentEmployer || '',
      employmentStatus: tenantData.employmentStatus || '',
      proposedMoveInDate: tenantData.proposedMoveInDate || '',
      numberOfOccupants: tenantData.numberOfOccupants || '',
      hasPets: tenantData.hasPets || null,
      smokingStatus: tenantData.smokingStatus || '',
      priorEvictions: tenantData.priorEvictions || null,
      additionalNotes: tenantData.additionalNotes || '',
      // Store reference to original tenant
      originalTenantId: tenant.id,
      originalTenantName: tenantData.fullName || 'Unknown Tenant'
    };
    
    // Set the form data and switch to submit tab
    setFormData(preFilledData);
    setActiveTab('submit');
    
    // Show success message
    setUploadSuccess('✅ Application pre-filled with tenant information! You can modify any details before submitting.');
    setTimeout(() => setUploadSuccess(null), 5000);
  };

  // Make handler available globally for child components
  useEffect(() => {
    window.handleCreateApplicationForTenant = handleCreateApplicationForTenant;
    return () => {
      delete window.handleCreateApplicationForTenant;
    };
  }, [activeTenants]);

  const handleCancelApplication = async (applicantId) => {
    const confirmed = window.confirm(
      'Cancel this application? It will be removed from your list and the homeowner applicant tab.'
    );
    if (!confirmed) return;

    setCancelingApplicantId(applicantId);
    try {
      await deleteDoc(doc(db, 'applicants', applicantId));
      try {
        await deleteObject(ref(storage, `applications/${user.uid}/${applicantId}.pdf`));
      } catch (storageError) {
        if (storageError?.code !== 'storage/object-not-found') {
          throw storageError;
        }
      }
    } catch (error) {
      console.error('Error canceling application:', error);
      alert('Could not cancel application: ' + error.message);
    } finally {
      setCancelingApplicantId(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Top Header */}
      <RealtorHeader userData={userData} user={user} onLogout={onLogout} />

      <div className="dashboard-container">
        {/* Mobile toggle */}
        <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
          {mobileMenuOpen ? '✕' : '☰'}
        </button>

        {mobileMenuOpen && <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)} />}

        {/* Sidebar */}
        <div className={`dashboard-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
          <h2>Realtor Portal</h2>
          <div className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => nav('dashboard')}>
            🏠 Dashboard
          </div>
          <div className={`nav-item ${activeTab === 'submit' ? 'active' : ''}`} onClick={() => nav('submit')}>
            📤 Create Application
          </div>
          <div className={`nav-item ${activeTab === 'track' ? 'active' : ''}`} onClick={() => nav('track')}>
            📋 My Applications{applicants.length > 0 ? ` (${applicants.length})` : ''}
          </div>
          <div className={`nav-item ${activeTab === 'relationships' ? 'active' : ''}`} onClick={() => nav('relationships')}>
            👥 Active Relationships
          </div>
          <div className={`nav-item ${activeTab === 'invite' ? 'active' : ''}`} onClick={() => nav('invite')}>
            📧 Invite Owner
          </div>
          <div className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => nav('settings')}>
            Settings
          </div>
          <div className="nav-item logout-nav-item" onClick={() => { setMobileMenuOpen(false); onLogout(); }}>
            🚪 Sign Out
          </div>
          <div className="sidebar-footer">{userData?.fullName || user?.email}</div>
        </div>

      {/* Main content */}
      <div className="dashboard-content">

        {/* Dashboard tab */}
        {activeTab === 'dashboard' && (
          <div>
            <SectionIntro
              eyebrow={greetingLabel}
              title={`Welcome back, ${realtorFirstName}`}
              description="This is your home base for owner relationships, incoming applications, and the packets still waiting on a decision."
            />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
              {[
                {
                  label: 'Applications',
                  hint: 'Packets you submitted (not yet converted)',
                  value: applicants.length,
                  icon: '📋',
                  color: '#6366f1',
                  lines: [
                    `${pendingCount} awaiting owner review`,
                    `${approvedOnlyCount} approved, waiting on next step`,
                  ],
                  onNavigate: () => nav('track'),
                },
                {
                  label: 'Owners',
                  hint: 'Connected landlords you work with',
                  value: mergedConnectedOwners.length,
                  icon: '🏢',
                  color: '#0ea5e9',
                  lines: [
                    `${vacantUnits.length} vacant unit${vacantUnits.length === 1 ? '' : 's'} ready for applicants`,
                    `${ownersWithPlacementsCount} with tenants placed${ownersNoPlacementsYet ? ` · ${ownersNoPlacementsYet} connected, no placement yet` : ''}`,
                  ],
                  onNavigate: () => {
                    setRelationshipsView('owners');
                    nav('relationships');
                  },
                },
                {
                  label: 'Placements',
                  hint: 'Tenants you placed that still match their unit',
                  value: loadingActiveTenants ? '—' : activeTenants.length,
                  icon: '👥',
                  color: '#8b5cf6',
                  lines: loadingActiveTenants
                    ? ['Loading…']
                    : [
                        `${activeTenants.length} active placement${activeTenants.length === 1 ? '' : 's'}`,
                        `${pastPlacements.length} past on file`,
                      ],
                  onNavigate: () => {
                    setRelationshipsView('placements');
                    nav('relationships');
                  },
                },
              ].map(({ label, hint, value, icon, color, lines, onNavigate }) => {
                const clickable = typeof onNavigate === 'function';
                const isPlaceholder = value === '—';
                return (
                  <div
                    key={label}
                    role={clickable ? 'button' : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    onClick={clickable ? onNavigate : undefined}
                    onKeyDown={
                      clickable
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onNavigate();
                            }
                          }
                        : undefined
                    }
                    title={clickable ? `Open ${label === 'Applications' ? 'My Applications' : 'Active Relationships'}` : undefined}
                    style={{
                      background: '#fff',
                      borderRadius: 14,
                      padding: '20px 24px',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                      cursor: clickable ? 'pointer' : 'default',
                      outline: 'none',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ color: '#6b7280', fontSize: 12, fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</p>
                        <p style={{ fontSize: 32, fontWeight: 800, color: isPlaceholder ? '#9ca3af' : '#111827', margin: '0 0 8px 0', lineHeight: 1.1 }}>{value}</p>
                        {hint && (
                          <p style={{ margin: '0 0 10px 0', fontSize: 12, color: '#9ca3af', lineHeight: 1.4 }}>{hint}</p>
                        )}
                        <ul style={{ margin: 0, paddingLeft: 18, color: '#64748b', fontSize: 13, lineHeight: 1.55 }}>
                          {(lines || []).map((line) => (
                            <li key={line} style={{ marginBottom: 2 }}>{line}</li>
                          ))}
                        </ul>
                        {clickable && (
                          <p style={{ margin: '12px 0 0 0', fontSize: 12, fontWeight: 600, color: '#6366f1' }}>Open →</p>
                        )}
                      </div>
                      <div style={{ width: 48, height: 48, flexShrink: 0, borderRadius: 12, fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', background: color + '18' }}>
                        {icon}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <VacantUnitsPanel vacancies={vacantUnits} onStartSubmitting={() => setActiveTab('submit')} />
          </div>
        )}

        {/* Submit tab */}
        {activeTab === 'submit' && (
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, border: '1px solid #e5e7eb', boxShadow: '0 1px 8px rgba(0,0,0,0.05)' }}>
            <SectionIntro
              eyebrow="Application Intake"
              title="Create a rental application"
              description="Select a connected owner or enter their email to send the application. Add tenant details and let Tenant Dispatch organize everything for review."
            />

            {uploadSuccess && (
              <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 10, padding: '14px 18px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20 }}>✅</span>
                <div>
                  <p style={{ color: '#065f46', fontWeight: 700, margin: 0 }}>Application submitted!</p>
                  <p style={{ color: '#047857', fontSize: 13, margin: 0 }}>Check "My Applications" to track its status.</p>
                </div>
              </div>
            )}

            <UploadZone
              realtorId={user?.uid}
              realtorEmail={user?.email}
              registeredOwners={registeredOwners}
              formData={formData}
              setFormData={setFormData}
              activeTenants={activeTenants}
              onUploaded={(applicantId) => {
                setUploadSuccess(`✅ Application created! ID: ${applicantId}`);
                setActiveTab('track');
                setTimeout(() => setUploadSuccess(null), 5000);
              }}
            />
            {registeredOwnersError && (
              <div style={{ marginTop: 16, background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 10, padding: '14px 18px' }}>
                <p style={{ color: '#9a3412', fontWeight: 700, margin: 0 }}>{registeredOwnersError}</p>
              </div>
            )}
          </div>
        )}

        {/* Track tab */}
        {activeTab === 'track' && (
          <div>
            <SectionIntro
              eyebrow="Application Tracking"
              title="Monitor every packet you send"
              description="See exactly where each application stands, confirm which owner received it, and cancel anything that needs to be corrected before a decision is made."
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <h2 style={{ margin: 0, color: '#111827', fontSize: 20, fontWeight: 700 }}>My Submitted Applications</h2>
              {applicants.length > 0 && <span style={{ color: '#6b7280', fontSize: 14 }}>{applicants.length} total</span>}
            </div>

            {loadingApplicants ? (
              <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>⚙️</div>
                <p>Loading applications…</p>
              </div>
            ) : applicants.length === 0 ? (
              <div style={{ background: '#fff', borderRadius: 16, padding: 60, textAlign: 'center', border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
                <h3 style={{ color: '#374151', marginBottom: 8 }}>No applications yet</h3>
                <p style={{ color: '#9ca3af', marginBottom: 24 }}>Submit your first rental application to get started.</p>
                <button
                  onClick={() => setActiveTab('submit')}
                  style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
                >
                  Submit First Application
                </button>
              </div>
            ) : (
              <div>
                {applicants.map((applicant) => (
                  <ApplicantRow
                    key={applicant.id}
                    applicant={applicant}
                    id={applicant.id}
                    onCancel={handleCancelApplication}
                    canceling={cancelingApplicantId === applicant.id}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Active Relationships tab */}
        {activeTab === 'relationships' && (
          <div>
            <SectionIntro
              eyebrow="Relationship Management"
              title="Owners and placements you support"
              description="Property owners are your ongoing clients. Current placements reflect who is still tied to a unit (updates when an owner removes a tenant). Past placements are historical. Tenant contact details are not shown here by default."
            />
            
            {/* Statistics Cards */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: 16, 
              marginBottom: 24 
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #10b981, #059669)',
                color: '#fff',
                padding: '20px',
                borderRadius: 12,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 32, fontWeight: 700, marginBottom: 4 }}>
                  {activeTenants.length}
                </div>
                <div style={{ fontSize: 14, opacity: 0.9 }}>
                  Current placements
                </div>
              </div>
              <div style={{
                background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                color: '#fff',
                padding: '20px',
                borderRadius: 12,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 32, fontWeight: 700, marginBottom: 4 }}>
                  {activeOwners.length}
                </div>
                <div style={{ fontSize: 14, opacity: 0.9 }}>
                  Connected Owners
                </div>
              </div>
              <div style={{
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                color: '#fff',
                padding: '20px',
                borderRadius: 12,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 32, fontWeight: 700, marginBottom: 4 }}>
                  {activeTenants.length + pastPlacements.length}
                </div>
                <div style={{ fontSize: 14, opacity: 0.9 }}>
                  Conversions (all time)
                </div>
              </div>
            </div>

            {/* View Toggle and Search */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: 20,
              flexWrap: 'wrap',
              gap: 16
            }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => setRelationshipsView('owners')}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: relationshipsView === 'owners'
                      ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                      : '#f3f4f6',
                    color: relationshipsView === 'owners' ? '#fff' : '#374151',
                    transition: 'all 0.2s'
                  }}
                >
                  🏢 By owners ({activeOwners.length})
                </button>
                <button
                  type="button"
                  onClick={() => setRelationshipsView('placements')}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: relationshipsView === 'placements'
                      ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                      : '#f3f4f6',
                    color: relationshipsView === 'placements' ? '#fff' : '#374151',
                    transition: 'all 0.2s'
                  }}
                >
                  📌 Current placements ({activeTenants.length})
                </button>
                <button
                  type="button"
                  onClick={() => setRelationshipsView('past')}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: relationshipsView === 'past'
                      ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                      : '#f3f4f6',
                    color: relationshipsView === 'past' ? '#fff' : '#374151',
                    transition: 'all 0.2s'
                  }}
                >
                  📜 Past placements ({pastPlacements.length})
                </button>
              </div>
              
              <input
                type="text"
                placeholder="Search by owner, unit, or application ID…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: 8,
                  fontSize: 14,
                  minWidth: 250,
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Content based on selected view */}
            {relationshipsView === 'placements' && (
              <div>
                <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: 18, fontWeight: 700 }}>
                  Current placements ({activeTenants.length})
                </h3>
                {loadingActiveTenants ? (
                  <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
                    <div style={{ fontSize: 32, marginBottom: 12 }}>⚙️</div>
                    <p>Loading placements…</p>
                  </div>
                ) : activeTenants.length === 0 ? (
                  <div style={{ background: '#fff', borderRadius: 16, padding: 60, textAlign: 'center', border: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>📌</div>
                    <h3 style={{ color: '#374151', marginBottom: 8 }}>No current placements</h3>
                    <p style={{ color: '#9ca3af', marginBottom: 24 }}>When an owner converts an application and the tenant is still on the unit, it appears here. If someone moves out, the owner removes them from the unit and this list updates.</p>
                  </div>
                ) : (
                  <div>
                    {activeTenants
                      .filter((row) => matchesRelationshipsSearch(row, searchTerm))
                      .map((tenant) => (
                        <ActiveTenantRow
                          key={tenant.id}
                          tenant={tenant}
                        />
                      ))}
                  </div>
                )}
              </div>
            )}

            {relationshipsView === 'past' && (
              <div>
                <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: 18, fontWeight: 700 }}>
                  Past placements ({pastPlacements.length})
                </h3>
                {loadingActiveTenants ? (
                  <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
                    <div style={{ fontSize: 32, marginBottom: 12 }}>⚙️</div>
                    <p>Loading…</p>
                  </div>
                ) : pastPlacements.length === 0 ? (
                  <div style={{ background: '#fff', borderRadius: 16, padding: 60, textAlign: 'center', border: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>📜</div>
                    <h3 style={{ color: '#374151', marginBottom: 8 }}>No past placements yet</h3>
                    <p style={{ color: '#9ca3af' }}>Ended placements (owner removed tenant from unit, or tenant no longer matches the unit) show here for your records—without tenant contact details.</p>
                  </div>
                ) : (
                  <div>
                    {pastPlacements
                      .filter((row) => matchesRelationshipsSearch(row, searchTerm))
                      .map((tenant) => (
                        <PastPlacementRow key={tenant.id} tenant={tenant} />
                      ))}
                  </div>
                )}
              </div>
            )}

            {relationshipsView === 'owners' && (
              <div>
                <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: 18, fontWeight: 700 }}>
                  Connected owners ({mergedConnectedOwners.length})
                </h3>
                {loadingActiveOwners ? (
                  <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
                    <div style={{ fontSize: 32, marginBottom: 12 }}>⚙️</div>
                    <p>Loading connected owners…</p>
                  </div>
                ) : mergedConnectedOwners.length === 0 ? (
                  <div style={{ background: '#fff', borderRadius: 16, padding: 60, textAlign: 'center', border: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>🏢</div>
                    <h3 style={{ color: '#374151', marginBottom: 8 }}>No connected owners yet</h3>
                    <p style={{ color: '#9ca3af', marginBottom: 24 }}>Invite an owner from the Invite tab and have them accept, or convert an applicant to a tenant.</p>
                    <button
                      type="button"
                      onClick={() => setActiveTab('invite')}
                      style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', marginRight: 10 }}
                    >
                      Invite an owner
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('submit')}
                      style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', padding: '12px 24px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
                    >
                      Create application
                    </button>
                  </div>
                ) : (
                  <div>
                    {mergedConnectedOwners
                      .filter((relationship) => {
                        const ownerName = relationship.ownerDetails?.fullName || '';
                        const ownerEmail = relationship.ownerDetails?.email || '';
                        const invitedEmail = relationship.ownerEmail || '';
                        const q = (searchTerm || '').toLowerCase();
                        return (
                          ownerName.toLowerCase().includes(q) ||
                          ownerEmail.toLowerCase().includes(q) ||
                          invitedEmail.toLowerCase().includes(q)
                        );
                      })
                      .map((relationship) => (
                        <ActiveOwnerRow
                          key={relationship.id}
                          relationship={relationship}
                        />
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Invite Owner tab */}
        {activeTab === 'invite' && (
          <div>
            <SectionIntro
              eyebrow="Owner Invitations"
              title="Invite an owner into your workflow"
              description="Send the owner a join link, then wait for them to approve the relationship before routing applications to their dashboard."
            />
            <InviteOwnerTab realtorId={user?.uid} realtorName={userData?.fullName || user?.email} userData={userData} />
          </div>
        )}

        {activeTab === 'settings' && (
          <RealtorSettingsTab user={user} userData={userData} />
        )}
      </div>
      </div>
    </div>
  );
}
